import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRightLeft } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Conversion factor: 1 mmol/L = 18.018 mg/dL
const CONVERSION_FACTOR = 18.018;

const GMRInput = ({ 
  value, 
  unit = 'mg/dL', 
  onChange, 
  onUnitChange,
  label = 'Blood Glucose (GMR)',
  disabled = false,
  className = ''
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [localUnit, setLocalUnit] = useState(unit);
  const [isConverting, setIsConverting] = useState(false);

  // Sync with parent value
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  useEffect(() => {
    setLocalUnit(unit);
  }, [unit]);

  // Convert value between units
  const convertValue = (val, fromUnit, toUnit) => {
    if (!val || isNaN(parseFloat(val))) return '';
    
    const numVal = parseFloat(val);
    
    if (fromUnit === toUnit) return val;
    
    if (fromUnit === 'mg/dL' && toUnit === 'mmol/L') {
      return (numVal / CONVERSION_FACTOR).toFixed(1);
    } else if (fromUnit === 'mmol/L' && toUnit === 'mg/dL') {
      return Math.round(numVal * CONVERSION_FACTOR).toString();
    }
    
    return val;
  };

  // Handle unit toggle
  const handleToggleUnit = () => {
    const newUnit = localUnit === 'mg/dL' ? 'mmol/L' : 'mg/dL';
    const convertedValue = convertValue(localValue, localUnit, newUnit);
    
    setLocalUnit(newUnit);
    setLocalValue(convertedValue);
    
    if (onChange) onChange(convertedValue);
    if (onUnitChange) onUnitChange(newUnit);
  };

  // Handle value change
  const handleValueChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (onChange) onChange(newValue);
  };

  // Get reference ranges
  const getReferenceRange = () => {
    if (localUnit === 'mg/dL') {
      return 'Normal: 70-100 mg/dL (fasting)';
    } else {
      return 'Normal: 3.9-5.6 mmol/L (fasting)';
    }
  };

  // Determine if value is out of range
  const getValueStatus = () => {
    if (!localValue || isNaN(parseFloat(localValue))) return null;
    
    const numVal = parseFloat(localValue);
    
    if (localUnit === 'mg/dL') {
      if (numVal < 70) return 'low';
      if (numVal > 100 && numVal <= 125) return 'elevated';
      if (numVal > 125) return 'high';
    } else {
      if (numVal < 3.9) return 'low';
      if (numVal > 5.6 && numVal <= 6.9) return 'elevated';
      if (numVal > 6.9) return 'high';
    }
    return 'normal';
  };

  const status = getValueStatus();
  const statusColors = {
    low: 'border-yellow-500 bg-yellow-50',
    normal: 'border-green-500 bg-green-50',
    elevated: 'border-amber-500 bg-amber-50',
    high: 'border-red-500 bg-red-50'
  };

  return (
    <div className={className}>
      <Label className="flex items-center gap-2">
        {label}
        <span className="text-xs text-slate-500">({localUnit})</span>
      </Label>
      <div className="flex items-center gap-2 mt-1">
        <div className="relative flex-1">
          <Input
            type="number"
            step={localUnit === 'mmol/L' ? '0.1' : '1'}
            value={localValue}
            onChange={handleValueChange}
            disabled={disabled}
            placeholder={localUnit === 'mg/dL' ? 'e.g., 95' : 'e.g., 5.3'}
            className={`pr-16 ${status ? statusColors[status] : ''}`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            {localUnit}
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleToggleUnit}
          disabled={disabled}
          className="gap-1 px-2"
          title={`Convert to ${localUnit === 'mg/dL' ? 'mmol/L' : 'mg/dL'}`}
        >
          <ArrowRightLeft className="w-4 h-4" />
          {localUnit === 'mg/dL' ? 'mmol/L' : 'mg/dL'}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">{getReferenceRange()}</p>
      {status && localValue && (
        <p className={`text-xs mt-1 ${
          status === 'low' ? 'text-yellow-600' :
          status === 'normal' ? 'text-green-600' :
          status === 'elevated' ? 'text-amber-600' :
          'text-red-600'
        }`}>
          {status === 'low' && '⚠️ Low blood glucose'}
          {status === 'normal' && '✓ Normal range'}
          {status === 'elevated' && '⚠️ Elevated (pre-diabetic range)'}
          {status === 'high' && '⚠️ High (diabetic range)'}
        </p>
      )}
    </div>
  );
};

export default GMRInput;
